import { axiosClient } from '@/shared/apis/config';
import { ApiResponse } from '@/shared/types';
import { CustomQueryOptions } from '@/shared/types/global/react-query/query';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  DtoContentStatus,
  DtoContentType,
  DtoExtension,
  DtoFolderItemType,
} from '@/shared/constants';

export const GetFolderItemContentsCursorRequestScheme = z.object({
  spaceId: z.number().optional(),
  contentTypeList: z.array(z.nativeEnum(DtoContentType)).optional(),
  cursorId: z.string().optional(),
  projectId: z.number().optional(),
  size: z.number().optional(),
  sort: z.string().optional(),
});

export const UserFolderItemContentScheme = z.object({
  id: z.number(),
  name: z.string(),
  type: z.nativeEnum(DtoFolderItemType),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  itemId: z.number(),
  publicUrl: z.string().optional(),
  thumbnailPublicUrl: z.string().optional(),
  contentType: z.nativeEnum(DtoContentType),
  extension: z.nativeEnum(DtoExtension),
  status: z.nativeEnum(DtoContentStatus),
  duration: z.number().optional(),
});

export type UserFolderItemContent = z.infer<typeof UserFolderItemContentScheme>;
export type GetFolderItemContentsCursorRequest = z.infer<
  typeof GetFolderItemContentsCursorRequestScheme
>;

type GetFolderItemContentsCursorResponse = ApiResponse<UserFolderItemContent[]>;

export const getFolderItemContentsCursorApi = async (
  params: GetFolderItemContentsCursorRequest,
) => {
  try {
    const response = await axiosClient.get<GetFolderItemContentsCursorResponse>(
      '/v1.0/folder/item/contents/cursor',
      {
        params,
      },
    );

    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const QueryKeyUseGetFolderItemContentsCursorApi =
  'GetFolderItemContentsCursorApi' as const;

export const useGetFolderItemContentsCursorApi = (
  params: GetFolderItemContentsCursorRequest,
  options?: CustomQueryOptions<GetFolderItemContentsCursorResponse>,
) => {
  const { queryKey, ...rest } = options ?? {};

  return useQuery({
    queryKey: [
      QueryKeyUseGetFolderItemContentsCursorApi,
      params,
      ...(queryKey ?? []),
    ],
    queryFn: async () => await getFolderItemContentsCursorApi(params),
    ...rest,
  });
};
